/**
 * YuklemeDugmesi — telefondan belge yükleme denetimi (backlog #646): "Fotoğraf çek" (arka kamera) ve "Dosya / galeriden seç" (resim ya da PDF).
 *
 * Seçilen dosya SUNUCUYA GİTMEDEN önce denetlenir (resim ya da PDF, boş değil, `maxMb` altında; mesajı açık). Yükleme sırasında ilerleme çubuğu görünür; sunucunun
 * reddi (tür, boyut, içerik, onaylı belge, hız sınırı) olduğu gibi gösterilir. Başarıda `onBitti()` çağrılır. Oturum bittiyse `onOturumBitti()`.
 * `onYukle(dosya, ilerlemeyiBildir)` bir söz döndürür.
 */
import React, { useRef, useState } from 'react';
import { Alert, Button, Progress } from 'reactstrap';
import { Camera, FolderOpen } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { dosyaDenetle } from '../../../services/kisiPanelService';

const YuklemeDugmesi = ({ onYukle, onBitti, onOturumBitti, maxMb = 10, devre = false, kimlik }) => {
  const { t } = useTranslation('kisiPanel');
  const dosyaRef = useRef(null);
  const kameraRef = useRef(null);
  const [yukleniyor, setYukleniyor] = useState(false);
  const [ilerleme, setIlerleme] = useState(0);
  const [hata, setHata] = useState(null);
  const [tamam, setTamam] = useState(false);

  async function sec(e) {
    const dosya = e.target.files && e.target.files[0];
    e.target.value = '';   // aynı dosya yeniden seçilebilsin
    if (!dosya) return;
    setHata(null);
    setTamam(false);
    const sorun = dosyaDenetle(dosya, maxMb);
    if (sorun) { setHata(t(`belge.hata.${sorun}`, { mb: maxMb })); return; }
    setYukleniyor(true);
    setIlerleme(0);
    try {
      await onYukle(dosya, setIlerleme);
      setTamam(true);
      if (onBitti) await onBitti();
    } catch (err) {
      if (err && err.oturumBitti) { onOturumBitti(); return; }
      setHata(err && err.network ? t('hata.baglanti') : (err && err.message) || t('belge.hata.yuklenemedi'));
    } finally {
      setYukleniyor(false);
    }
  }

  return (
    <div data-testid={`yukleme-${kimlik || 'dosya'}`}>
      <div className="d-flex flex-wrap gap-2">
        <Button color="primary" className="flex-fill" disabled={devre || yukleniyor} id={`yukleme-kamera-${kimlik || 'dosya'}`} onClick={() => kameraRef.current.click()}>
          <Camera size={16} className="me-1" />{t('belge.kamera')}
        </Button>
        <Button color="light" className="flex-fill" disabled={devre || yukleniyor} id={`yukleme-dosya-${kimlik || 'dosya'}`} onClick={() => dosyaRef.current.click()}>
          <FolderOpen size={16} className="me-1" />{t('belge.dosyaSec')}
        </Button>
      </div>
      <input ref={kameraRef} type="file" accept="image/*" capture="environment" hidden onChange={sec} data-testid={`yukleme-kamera-girdi-${kimlik || 'dosya'}`} />
      <input ref={dosyaRef} type="file" accept="image/*,application/pdf" hidden onChange={sec} data-testid={`yukleme-dosya-girdi-${kimlik || 'dosya'}`} />
      {yukleniyor && <Progress className="mt-2" value={ilerleme} animated data-testid="yukleme-ilerleme">{t('belge.yukleniyor', { yuzde: ilerleme })}</Progress>}
      {hata && <Alert color="danger" fade={false} className="mt-2 mb-0 py-2" data-testid="yukleme-hata">{hata}</Alert>}
      {tamam && !hata && <Alert color="success" fade={false} className="mt-2 mb-0 py-2" data-testid="yukleme-tamam">{t('belge.yuklendi')}</Alert>}
      <div className="form-text">{t('belge.ipucu', { mb: maxMb })}</div>
    </div>
  );
};

export default YuklemeDugmesi;
